'use client';

import {
  type HTMLAttributes,
  type ReactNode,
  useEffect,
  useState,
  createContext,
  useContext,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { motion, type PanInfo } from 'framer-motion';
import { cn } from '../lib/cn';

interface DialogContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const DialogContext = createContext<DialogContextValue | null>(null);

function useDialog(): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('Dialog components must be used within <Dialog>');
  return ctx;
}

export interface DialogProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}

export function Dialog({ open, defaultOpen = false, onOpenChange, children }: DialogProps): React.ReactElement {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = open !== undefined;
  const value = isControlled ? open : internalOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );
  return <DialogContext.Provider value={{ open: value, setOpen }}>{children}</DialogContext.Provider>;
}

export function DialogTrigger({
  children,
  asChild: _asChild,
}: {
  children: ReactNode;
  asChild?: boolean;
}): React.ReactElement {
  const { setOpen } = useDialog();
  return (
    <span onClick={() => setOpen(true)} className="inline-flex">
      {children}
    </span>
  );
}

export function DialogContent({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>): React.ReactElement | null {
  const { open, setOpen } = useDialog();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, setOpen]);
  if (!mounted || !open) return null;

  // Mobil: bottom-sheet, drag-to-dismiss enabled
  // Desktop: centered card, no drag (constraints + bg-black backdrop)
  const handleDragEnd = (_e: unknown, info: PanInfo): void => {
    if (info.offset.y > 120 || info.velocity.y > 600) {
      setOpen(false);
    }
  };

  // `...props` HTMLAttributes uyumlu olsa da framer'ın motion.div'i ek üyeler bekliyor,
  // bu yüzden sadece className + style + id gibi güvenli alanları aktarıyoruz.
  const { style, id } = props;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <motion.div
        aria-hidden="true"
        className="fixed inset-0 bg-black/50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        onClick={() => setOpen(false)}
      />
      <motion.div
        role="dialog"
        aria-modal="true"
        id={id}
        style={style}
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 40, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 380, damping: 32 }}
        drag="y"
        dragDirectionLock
        dragElastic={{ top: 0, bottom: 0.4 }}
        dragConstraints={{ top: 0, bottom: 0 }}
        onDragEnd={handleDragEnd}
        className={cn(
          'relative z-50 grid w-full max-w-lg gap-4 border border-border/60 bg-popover',
          'max-h-[92vh] overflow-y-auto overflow-x-hidden overscroll-contain',
          // Modern fintech: daha rounded, soft elevated shadow
          'rounded-t-2xl p-4 pb-[max(env(safe-area-inset-bottom),1rem)] shadow-xl',
          'sm:rounded-2xl sm:p-6 sm:pb-6 sm:max-h-[85vh]',
          className,
        )}
      >
        {/* Drag handle (mobile only) — kullanıcıya sürüklenebileceğini söyler */}
        <div
          aria-hidden="true"
          className="-mt-2 -mb-2 mx-auto h-1.5 w-12 cursor-grab rounded-full bg-muted-foreground/30 active:cursor-grabbing sm:hidden"
        />
        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Close"
          className="absolute right-3 top-3 rounded-lg p-1.5 text-muted-foreground transition-all hover:bg-accent hover:text-foreground sm:right-4 sm:top-4"
        >
          <X className="h-4 w-4" />
        </button>
        {children}
      </motion.div>
    </div>,
    document.body,
  );
}

export function DialogHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>): React.ReactElement {
  return <div className={cn('flex flex-col gap-1.5 text-left', className)} {...props} />;
}

export function DialogFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>): React.ReactElement {
  return (
    <div
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:space-x-2', className)}
      {...props}
    />
  );
}

export function DialogTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>): React.ReactElement {
  return <h2 className={cn('text-lg font-semibold leading-tight tracking-tight', className)} {...props} />;
}

export function DialogDescription({
  className,
  ...props
}: HTMLAttributes<HTMLParagraphElement>): React.ReactElement {
  return <p className={cn('text-sm text-muted-foreground', className)} {...props} />;
}

export function useDialogControls(): DialogContextValue {
  return useDialog();
}
